import { map } from '@store/selectors';

import { normalizeObject } from '@utils/common';

import { TAssetTypesDTO } from '@interfaces/map/dto/mapDTO';
import { PartialDetailedMapFiltersDTO } from '@interfaces/map/dto/mapFiltersDTO';

interface IAssetTypesWithSubtypes {
	assetTypeId: string;
	assetSubTypeIds?: string[];
}

export function getAssetTypesDtoForFilter(assetTypes: TAssetTypesDTO) {
	return assetTypes.map((assetType) => {
		if (typeof assetType === 'string') {
			return { assetTypeId: assetType };
		}

		const parentAssetType = Object.keys(assetType)[0] as keyof typeof assetType;
		const subtypeIds = assetType[parentAssetType]?.subTypes;

		return {
			assetTypeId: parentAssetType,
			assetSubTypeIds: subtypeIds,
		} as IAssetTypesWithSubtypes;
	});
}

export interface IGetAssetFiltersDtoReturnType {
	boroughIds: string[];
	polygonIds: string[];
	filter: {
		assetState: {
			isLinkedWithSite?: boolean;
			isOrganisationRent?: boolean;
			timeRentType?: string;
		};
		detailed?: PartialDetailedMapFiltersDTO;
		filter?: {
			assetOwnerIds?: string[];
			assetSiteASs?: string[];
			backhaulASs?: string[];
			powerSupplyASs?: string[];
			assetTypeWithSubTypes?: IAssetTypesWithSubtypes[];
		};
	};
}

export function getAssetFiltersDto(mapState: ReturnType<typeof map>): IGetAssetFiltersDtoReturnType {
	const {
		activeBoroughs,
		activeCustomPolygons,
		appliedAssetsFilterValues,
		isAcquiredAssetsShownApplied,
		appliedAcquiredAssetsTypeFilter,
	} = mapState;

	const filtersDto = {
		boroughIds: activeBoroughs,
		polygonIds: activeCustomPolygons,
		filter: {
			assetState: {
				...appliedAssetsFilterValues?.isMyLinked && { isLinkedWithSite: true },
				...isAcquiredAssetsShownApplied && { isOrganisationRent: true },
				...isAcquiredAssetsShownApplied && { timeRentType: appliedAcquiredAssetsTypeFilter },
			},
			...appliedAssetsFilterValues?.detailed && {
				detailed: {
					height: appliedAssetsFilterValues.detailed.height,
					pricing: appliedAssetsFilterValues.detailed.indicativeAcceptablePricing,
					siteAvailabilityReviewDate: appliedAssetsFilterValues.detailed.siteAvailabilityReviewDate,
					withoutHeight: appliedAssetsFilterValues.detailed.withoutHeight,
				},
			},
			filter: {
				assetOwnerIds: appliedAssetsFilterValues?.ownerId,
				assetSiteASs: appliedAssetsFilterValues.assetSiteAvailabilityStatus,
				backhaulASs: appliedAssetsFilterValues.backhaulAccessibilityStatus,
				powerSupplyASs: appliedAssetsFilterValues.powerSupplyAvailabilityStatus,
				...appliedAssetsFilterValues?.assetType && {
					assetTypeWithSubTypes: getAssetTypesDtoForFilter(appliedAssetsFilterValues.assetType),
				},
			},
		},
	};

	return normalizeObject(filtersDto);
}
